import { NavTarget } from './types';

/** Every KPI page lives under this path (manageb-routing's guarded kpi parent); the bare root lands on Admissions. */
export const KPI_ROOT = '/manageb/kpi';

export const TITLES = {
  createReferral: 'Create New Referral',
  referralInbox: 'Referral Inbox',
  managerDashboard: 'Manager Dashboard',
  kpiDashboard: 'KPI Dashboard',
  liveBedBoard: 'Live Bed Board',
  dischargePlan: 'Discharge Plan',
  liveCensus: 'Live Census',
  censusTrends: 'Census Trends',
  dailyTrendReport: 'Daily Trend Report',
  hospitalTracker: 'Hospital Tracker',
  returningReferrals: 'Returning Referrals',
  adtReport: 'ADT Report',
  kpis: 'KPIs',
  tripleCheckReport: 'Triple Check Report',
  averageLosReport: 'Average LOS Report',
  referralConversions: 'Referral Conversions',
  referralsSummary: 'Referrals Summary',
  referralResponseReport: 'Referral Response Report',
  declineInsights: 'Decline Insights',
  assessmentReport: 'Patient Assessment',
  payerChangeReport: 'Payer Change Report',
  complaiReports: 'ComplAI Reports',
  mdsReports: 'MDS Reports',
  mdsDashboard: 'MDS Dashboard',
  complaiDashboard: 'ComplAI Dashboard',
  eventReports: 'Event Based Reports',
  secondApproval: 'Second Approval',
  highRiskPatients: 'High Risk Patients',
  users: 'Users',
  corporateOverview: 'Corporate Overview',
  admin: 'Admin',
  profile: 'Profile',
  logOut: 'Log Out',
} as const;

export const CENSUS_TRENDS_PATH = '/manageb/census-reports/yearly-census';
export const MONTHLY_CENSUS_PATH = '/manageb/census-reports/monthly-census';

const internal = (to: string, state?: { [k: string]: any }): NavTarget => ({ kind: 'internal', to, state });

export const NAV_ROUTES: Record<string, NavTarget> = {
  [TITLES.managerDashboard]: internal('/manageb/manager-dashboard'),
  [TITLES.kpiDashboard]: internal('/manageb/kpi-dashboard'),
  [TITLES.liveBedBoard]: internal('/manageb/bed-boards'),
  [TITLES.dischargePlan]: internal('/manageb/discharge-plan'),
  [TITLES.liveCensus]: internal('/manageb/census'),
  [TITLES.createReferral]: internal('/manageb/manual-referral-form'),
  [TITLES.hospitalTracker]: internal('/manageb/hospital', { isPccNew: true }),
  [TITLES.returningReferrals]: internal('/manageb/reentry', { isPccNew: true }),
  [TITLES.adtReport]: internal('/manageb/adt-report', { isPccNew: true }),
  [TITLES.kpis]: internal(KPI_ROOT),
  [TITLES.tripleCheckReport]: internal('/manageb/triple-check-report'),
  [TITLES.averageLosReport]: internal('/manageb/average-los-report'),
  [TITLES.secondApproval]: internal('/manageb/second-approval-list', { isPccNew: true }),
  [TITLES.referralConversions]: internal('/manageb/referral-conversions', { isPccNew: true }),
  [TITLES.referralsSummary]: internal('/manageb/referrals-summary'),
  [TITLES.referralResponseReport]: internal('/manageb/referral-response-report', { isPccNew: true }),
  [TITLES.declineInsights]: internal('/manageb/decline-insights'),
  [TITLES.assessmentReport]: internal('/manageb/patient-assessments', { isPccNew: true }),
  [TITLES.users]: internal('/manageb/users', { isPccNew: true }),
  [TITLES.payerChangeReport]: internal('/manageb/reports', { isPccNew: true }),
  [TITLES.complaiReports]: internal('/manageb/report-builder'),
  [TITLES.mdsReports]: internal('/manageb/mds-report-builder'),
  [TITLES.mdsDashboard]: internal('/manageb/mds-report-signals'),
  [TITLES.complaiDashboard]: internal('/manageb/report-signals'),
  [TITLES.eventReports]: internal('/manageb/event-reports'),
  [TITLES.censusTrends]: internal(CENSUS_TRENDS_PATH, { isPccNew: true }),
  [TITLES.dailyTrendReport]: internal(MONTHLY_CENSUS_PATH),
  [TITLES.profile]: internal('/client'),
  [TITLES.referralInbox]: internal('/manageb/dashboard'),
  [TITLES.highRiskPatients]: internal('/manageb/high-risk-patients'),
  [TITLES.corporateOverview]: internal('/manageb/global-portal'),
};

/**
 * Detail pages the nav never lists as screens. Each borrows the trail of the screen it opens
 * from and appends its own leaf, so `/manageb/info/9063878` reads
 * `Referral Inbox > Referral details`.
 */
export const DETAIL_ROUTES: Array<{ prefix: string; parentPath: string; title: string }> = [
  { prefix: '/manageb/info', parentPath: '/manageb/dashboard', title: 'Referral details' },
  { prefix: '/manageb/high-risk-patients', parentPath: '/manageb/bed-boards', title: TITLES.highRiskPatients },
];

/**
 * Entries that stand for every page under them: the "KPIs" entry is each KPI's
 * page (Admissions, Census, an admin's own). A visit to any of them is a
 * visit to the hub, for recents; every other entry is matched exactly.
 */
export const NAV_HUBS: readonly string[] = [KPI_ROOT];

/** The path a visit is remembered under: its hub's, or its own. */
export function recentPathOf(path: string): string {
  return NAV_HUBS.find(hub => path === hub || path.startsWith(`${hub}/`)) ?? path;
}

export const ADMIN_LANDING_FULL = '/manageb/admin/dashboard';
export const ADMIN_LANDING_LIMITED = '/manageb/admin/ai-decision-logic';

/** Roles 152, 145 and 190 get the limited (region-admin) set - land them on a page they can access. */
export function isLimitedAdminRole(role: number): boolean {
  return role === 152 || role === 145 || role === 190;
}

export function targetForTitle(title: string, role: number): NavTarget {
  if (title === TITLES.admin) {
    return internal(isLimitedAdminRole(role) ? ADMIN_LANDING_LIMITED : ADMIN_LANDING_FULL, { isPccNew: true });
  }
  return NAV_ROUTES[title] ?? internal('');
}

export function pathOf(target: NavTarget): string {
  return target.kind === 'internal' ? target.to : target.href;
}
